import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, SITE_URL } from '../../hooks/useSEO';

const CompleteSecurityManagementPage = () => {
  useSEO({
    title: 'Complete Security Management for Mortgage Firms — $179/mo | Mortgage Moat',
    description: 'All-inclusive cybersecurity management for mortgage companies at $179/month. Includes audits, training, policies, vulnerability scanning, incident response, and compliance reporting.',
    keywords: 'complete security management, mortgage cybersecurity bundle, all-inclusive security, GLBA compliance management, cybersecurity subscription',
    canonical: '/complete-security-management',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Complete Security Management',
        provider: { '@type': 'Organization', name: 'Mortgage Moat', url: SITE_URL },
        url: `${SITE_URL}/complete-security-management`,
        description: 'All-inclusive monthly cybersecurity management covering policies, audits, training, vulnerability scanning, incident response, and compliance for mortgage companies.',
        offers: {
          '@type': 'Offer',
          price: '179',
          priceCurrency: 'USD',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '179',
            priceCurrency: 'USD',
            unitText: 'month',
          },
        },
        areaServed: { '@type': 'Country', name: 'United States' },
        serviceType: 'Managed Security Service',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Complete Security Management', item: `${SITE_URL}/complete-security-management` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How is Complete Security Management different from other services?',
            acceptedAnswer: { '@type': 'Answer', text: 'Complete Security Management bundles all individual services into one comprehensive package at a significant discount. Instead of paying separately for risk assessments, training, policies, and managed services, you get everything for one monthly price.' },
          },
          {
            '@type': 'Question',
            name: 'Is there a long-term contract for Complete Security Management?',
            acceptedAnswer: { '@type': 'Answer', text: 'No. While we recommend staying subscribed for continuous protection, you can cancel anytime with 30 days notice. We also offer a 30-day money-back guarantee if you\'re not satisfied.' },
          },
          {
            '@type': 'Question',
            name: 'How many employees does Complete Security Management cover?',
            acceptedAnswer: { '@type': 'Answer', text: 'The base subscription covers your organization regardless of size. Training is included for all employees, and we scale our monitoring and support to match your needs.' },
          },
        ],
      },
    ],
  });

  const allIncludes = [
    {
      icon: 'ri-file-text-line',
      title: 'Document Reviews & Policy Management',
      description: 'Comprehensive review and maintenance of all security documentation, policies, and procedures'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Regular Security Audits',
      description: 'Quarterly comprehensive security assessments to identify and address vulnerabilities'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Employee Awareness Training',
      description: 'Ongoing security training for all staff with phishing simulations and compliance tracking'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'IT/Cyber Security Policies & Procedures',
      description: 'Complete, customizable security policy manual covering all regulatory requirements'
    },
    {
      icon: 'ri-bug-line',
      title: 'Vulnerability Management & Scanning',
      description: 'Continuous monitoring, scanning, and patching of security vulnerabilities across all systems'
    },
    {
      icon: 'ri-first-aid-kit-line',
      title: 'Incident Mitigation & Remediation',
      description: '24/7 incident response team ready to contain threats and guide recovery efforts'
    },
    {
      icon: 'ri-file-shield-2-line',
      title: 'Compliance Monitoring & Reporting',
      description: 'Continuous compliance tracking with automated reporting for GLBA, FFIEC, and other regulations'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Monthly Security Assessments',
      description: 'Regular security posture evaluations with detailed findings and recommendations'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Policy Updates & Maintenance',
      description: 'Automatic updates to policies and procedures as regulations and threats evolve'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Priority Support & Consultation',
      description: 'Direct access to security experts via phone, email, and dedicated support portal'
    },
    {
      icon: 'ri-tools-line',
      title: 'Remediation Guidance',
      description: 'Step-by-step assistance implementing security improvements and fixing vulnerabilities'
    },
    {
      icon: 'ri-presentation-line',
      title: 'Executive Reporting',
      description: 'Monthly board-ready reports showing security posture, incidents, and compliance status'
    }
  ];

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'All-Inclusive Pricing',
      description: 'One monthly fee covers everything – no surprise charges or hidden costs'
    },
    {
      icon: 'ri-time-line',
      title: 'Save Time & Resources',
      description: 'Eliminate the need to manage multiple vendors and coordinate different services'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Complete Protection',
      description: 'Every aspect of your security program managed by experts who know mortgage compliance'
    },
    {
      icon: 'ri-team-line',
      title: 'Dedicated Security Team',
      description: 'Your own team of cybersecurity professionals available whenever you need them'
    }
  ];

  const comparison = [
    {
      approach: 'Traditional Approach',
      items: [
        { text: 'Risk Assessment: $299-$499 each', negative: true },
        { text: 'Training Platform: $25-$49/user/month', negative: true },
        { text: 'Policy Manual: $395 one-time', negative: true },
        { text: 'Managed Services: $20+/user/month', negative: true },
        { text: 'Incident Response: $249+ per incident', negative: true },
        { text: 'Compliance Consulting: $100+/hour', negative: true },
        { text: 'Multiple vendors to manage', negative: true },
        { text: 'Coordination headaches', negative: true },
        { text: 'Total: $500-$1,000+/month for small team', negative: true }
      ]
    },
    {
      approach: 'Complete Security Management',
      items: [
        { text: 'Everything included in one package', negative: false },
        { text: 'Document reviews & audits', negative: false },
        { text: 'Employee training & testing', negative: false },
        { text: 'Complete policy management', negative: false },
        { text: 'Vulnerability scanning & patching', negative: false },
        { text: 'Incident response included', negative: false },
        { text: 'Unlimited expert consultation', negative: false },
        { text: 'Single point of contact', negative: false },
        { text: 'Total: Just $179/month', negative: false }
      ]
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Initial Onboarding',
      description: 'We assess your current security posture and customize our services to your needs'
    },
    {
      step: '2',
      title: 'Policy Implementation',
      description: 'Deploy comprehensive security policies and procedures tailored to your organization'
    },
    {
      step: '3',
      title: 'Team Training Launch',
      description: 'Enroll employees in security awareness training with automated phishing simulations'
    },
    {
      step: '4',
      title: 'Continuous Monitoring',
      description: 'Ongoing vulnerability scanning, threat detection, and compliance monitoring begins'
    },
    {
      step: '5',
      title: 'Regular Assessments',
      description: 'Monthly security reviews and quarterly comprehensive audits keep you protected'
    },
    {
      step: '6',
      title: 'Ongoing Support',
      description: 'Continuous policy updates, incident response, and expert consultation as needed'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 to-red-800 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold mb-6">
              COMPLETE SECURITY MANAGEMENT
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Overwhelmed by Security? Let Us Handle Everything
            </h1>
            <p className="text-xl mb-8 text-white/90">
              Get complete cybersecurity management for your mortgage business – from policies to monitoring to training – all in one comprehensive package.
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 inline-block">
              <div className="text-6xl font-bold mb-2">$179<span className="text-2xl">/month</span></div>
              <div className="text-lg text-white/90">Everything your mortgage business needs to stay secure and compliant</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/purchase/managed-services"
                className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Subscribe Now
                <i className="ri-arrow-right-line"></i>
              </a>
              <a 
                href="/schedule-call"
                className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Schedule Consultation
                <i className="ri-calendar-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Everything You Need, Nothing You Don't
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A complete security program that covers every aspect of protecting your mortgage business – all managed by experts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allIncludes.map((item, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-xl mb-6">
                  <i className={`${item.icon} text-3xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Comparison */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              The Smart Way to Manage Security
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how much you save by consolidating all your security needs into one comprehensive package.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {comparison.map((column, colIndex) => (
              <div key={colIndex} className={`rounded-2xl shadow-xl overflow-hidden ${colIndex === 1 ? 'bg-gradient-to-br from-red-600 to-red-800 text-white' : 'bg-white'}`}>
                <div className={`text-center py-6 ${colIndex === 1 ? 'bg-black/20' : 'bg-gray-100'}`}>
                  <h3 className={`text-2xl font-bold ${colIndex === 1 ? 'text-white' : 'text-gray-900'}`}>
                    {column.approach}
                  </h3>
                </div>
                <div className="p-8">
                  <ul className="space-y-4">
                    {column.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.negative ? 'ri-close-line text-red-500' : 'ri-check-line'} text-xl ${colIndex === 1 ? 'text-white' : ''}`}></i>
                        </div>
                        <span className={`${colIndex === 1 ? 'text-white/90' : 'text-gray-700'} ${itemIndex === column.items.length - 1 ? 'font-bold text-lg' : ''}`}>
                          {item.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Complete Security Management?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-6">
                  <i className={`${benefit.icon} text-4xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Getting started is simple. We handle the heavy lifting while you focus on your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 flex items-center justify-center flex-shrink-0 bg-red-600 text-white text-3xl font-bold rounded-full mb-6">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            What You Get Every Month
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg">
                  <i className="ri-shield-check-line text-2xl text-red-600"></i>
                </div>
                Security & Protection
              </h3>
              <ul className="space-y-3">
                {[
                  'Continuous vulnerability scanning and patching',
                  'Monthly security posture assessments',
                  'Quarterly comprehensive security audits',
                  '24/7 incident response team availability',
                  'Dark web monitoring for data breaches',
                  'Threat intelligence and alerts',
                  'Network and endpoint security monitoring',
                  'Email security and phishing protection'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-green-500 text-xl"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg">
                  <i className="ri-file-shield-2-line text-2xl text-red-600"></i>
                </div>
                Compliance & Documentation
              </h3>
              <ul className="space-y-3">
                {[
                  'Complete IT/Cyber security policy manual',
                  'Regular policy updates and maintenance',
                  'GLBA and FFIEC compliance monitoring',
                  'Automated compliance reporting',
                  'Document reviews and approvals',
                  'Audit-ready documentation',
                  'Regulatory change notifications',
                  'Board-ready executive reports'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-green-500 text-xl"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg">
                  <i className="ri-graduation-cap-line text-2xl text-red-600"></i>
                </div>
                Training & Awareness
              </h3>
              <ul className="space-y-3">
                {[
                  'Unlimited employee security training',
                  'Monthly phishing simulations',
                  'Quarterly live training sessions',
                  'Custom training modules',
                  'Individual employee tracking',
                  'Automatic remedial training',
                  'Completion certificates',
                  'Training effectiveness reporting'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-green-500 text-xl"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg">
                  <i className="ri-customer-service-2-line text-2xl text-red-600"></i>
                </div>
                Support & Consultation
              </h3>
              <ul className="space-y-3">
                {[
                  'Dedicated account manager',
                  'Priority phone and email support',
                  'Unlimited expert consultation',
                  'Remediation guidance and assistance',
                  'Vendor risk management support',
                  'Security portal access',
                  'Monthly strategy calls',
                  'Emergency incident response'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-green-500 text-xl"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-2xl shadow-2xl overflow-hidden text-white">
            <div className="text-center py-12 px-6">
              <div className="inline-block bg-yellow-400 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-6">
                BEST VALUE
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Complete Security for Just $179/Month
              </h2>
              <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
                Everything your mortgage business needs to stay secure, compliant, and protected – all in one simple monthly subscription.
              </p>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 max-w-2xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-4xl font-bold mb-2">$179</div>
                    <div className="text-sm text-white/75">Per Month</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold mb-2">12</div>
                    <div className="text-sm text-white/75">Services Included</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold mb-2">24/7</div>
                    <div className="text-sm text-white/75">Expert Support</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/purchase/managed-services"
                  className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors w-full whitespace-nowrap cursor-pointer"
                >
                  Subscribe Now
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a 
                  href="/schedule-call"
                  className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors w-full whitespace-nowrap cursor-pointer"
                >
                  Schedule Consultation
                  <i className="ri-calendar-line"></i>
                </a>
              </div>

              <p className="text-sm text-white/75">
                No setup fees • Cancel anytime • 30-day money-back guarantee
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            {[
              {
                question: 'How is this different from your other services?',
                answer: 'Complete Security Management bundles all our individual services into one comprehensive package at a significant discount. Instead of paying separately for risk assessments, training, policies, and managed services, you get everything for one monthly price.'
              },
              {
                question: 'How quickly can we get started?',
                answer: 'Most clients are fully onboarded within one week. We\'ll conduct an initial assessment, deploy your security policies, enroll employees in training, and begin monitoring – all handled by our team.'
              },
              {
                question: 'Is there a long-term contract?',
                answer: 'No. While we recommend staying subscribed for continuous protection, you can cancel anytime with 30 days notice. We also offer a 30-day money-back guarantee if you\'re not satisfied.'
              },
              {
                question: 'What if we already have some security measures in place?',
                answer: 'Perfect! We\'ll assess your current security program and integrate with what\'s working while filling any gaps. We can replace redundant services or complement your existing tools.'
              },
              {
                question: 'How many employees does this cover?',
                answer: 'The base subscription covers your organization regardless of size. Training is included for all employees, and we scale our monitoring and support to match your needs.'
              },
              {
                question: 'What happens if there\'s a security incident?',
                answer: 'Our 24/7 incident response team springs into action immediately. We\'ll contain the threat, investigate the incident, guide remediation, and document everything for compliance – all included in your subscription.'
              },
              {
                question: 'Can we customize which services we receive?',
                answer: 'The package is designed to provide comprehensive protection, but we can adjust the focus based on your priorities. During onboarding, we\'ll discuss your specific needs and customize our approach.'
              },
              {
                question: 'How do we track what you\'re doing each month?',
                answer: 'You\'ll have access to a dedicated security portal showing all activities, findings, and status updates. Plus, you\'ll receive monthly executive reports and have regular calls with your account manager.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Stop Piecing Together Your Security Program
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Get everything you need in one comprehensive package. Simple pricing, complete protection, expert management.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/purchase/managed-services"
              className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors w-full whitespace-nowrap cursor-pointer"
            >
              Subscribe Now
              <i className="ri-arrow-right-line"></i>
            </a>
            <a 
              href="/schedule-call"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors w-full whitespace-nowrap cursor-pointer"
            >
              Talk to an Expert
              <i className="ri-user-voice-line"></i>
            </a>
          </div>
          <p className="text-sm text-white/75 mt-6">
            Questions? Call us at 844-662-8277 or email sales@mortgagemoat.com
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompleteSecurityManagementPage;